import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  exchangeCodeForTokens,
  createSessionFromTokens,
  setFederatedSession,
  signInWithGoogle,
} from "../../cognitoConfig";
import * as Sentry from "@sentry/react";
import { authService } from "./authService";
import { getErrorMessage, isNetworkError } from "../../utils/error-utils";
import { trackLogin } from "../../utils/analytics";
import styles from "./authorization.module.scss";
import classNames from "classnames/bind";

const cx = classNames.bind(styles);

const OAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [, setIsProcessing] = useState(true);
  const processedRef = React.useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Guard against processing the same code twice (React StrictMode, refresh, etc.)
      if (processedRef.current) return;
      processedRef.current = true;

      const code = searchParams.get("code");
      const errorParam = searchParams.get("error");
      const errorDescription = searchParams.get("error_description");

      if (errorParam) {
        // When the Pre Sign-up Lambda links a Google account to an existing
        // email/password user, it throws an error to prevent duplicate creation.
        // Cognito redirects here with that error. Auto-retry: the link is now
        // in place, so the second Google sign-in will succeed seamlessly.
        const isLinkingError =
          errorDescription?.includes("LINKED_EXISTING_USER") ||
          errorDescription?.includes("PreSignUp");
        if (isLinkingError) {
          signInWithGoogle();
          return;
        }

        setError(errorDescription || errorParam);
        setIsProcessing(false);
        return;
      }

      if (!code) {
        setError(t("oauth.noCode", "No authorization code received"));
        setIsProcessing(false);
        return;
      }

      try {
        // Exchange code for tokens
        const tokens = await exchangeCodeForTokens(code);

        // Create a Cognito session from the tokens
        const session = createSessionFromTokens(tokens);

        // Decode the ID token to get user info
        const idTokenPayload = session.getIdToken().decodePayload();
        const username = idTokenPayload.email || idTokenPayload.sub;

        // Set the session for this user
        setFederatedSession(username, session);

        // Now use the auth service to complete the login
        // This will authenticate with the backend and set up Redux state
        await authService.handleOAuthSession(session);
        trackLogin("google");

        // Redirect to returnTo or dashboard
        const returnTo = sessionStorage.getItem("auth_returnTo") || "/dashboard";
        sessionStorage.removeItem("auth_returnTo");
        navigate(returnTo);
      } catch (err: unknown) {
        // Suppress invalid_grant errors from Sentry — these are expected when
        // the OAuth code is reused (page reload) or expired. Not actionable.
        // Also suppress network errors — backend unreachable mid-callback.
        const msg = err instanceof Error ? err.message : String(err);
        if (!msg.includes("invalid_grant") && !isNetworkError(err)) {
          Sentry.captureException(err);
        }
        console.error("OAuth callback error:", err);
        setError(getErrorMessage(err) || t("oauth.failedSignIn", "Failed to complete sign in"));
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, dispatch]);

  if (error) {
    return (
      <div className={cx("authContainer")}>
        <div className={cx("formPanel")} style={{ width: "100%", maxWidth: "500px", margin: "auto" }}>
          <div className={cx("form")}>
            <div className={cx("formHeader")}>
              <h2 className={cx("formTitle")}>{t("oauth.signInFailed", "Sign In Failed")}</h2>
              <p className={cx("formSubtitle")}>{error}</p>
            </div>
            <button
              type="button"
              className={cx("submitButton")}
              onClick={() => navigate("/")}
            >
              {t("oauth.backToSignIn", "Back to Sign In")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cx("authContainer")}>
      <div className={cx("formPanel")} style={{ width: "100%", maxWidth: "500px", margin: "auto" }}>
        <div className={cx("form")}>
          <div className={cx("formHeader")}>
            <h2 className={cx("formTitle")}>{t("oauth.signingIn", "Signing you in...")}</h2>
            <p className={cx("formSubtitle")}>{t("oauth.pleaseWait", "Please wait while we complete your sign in.")}</p>
          </div>
          <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
            <span className={cx("spinner")}></span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OAuthCallback;
